import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('quickhop_token'));

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('quickhop_token');
    if (storedToken) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        setUser(response.data);
        setToken(storedToken);
      } catch (error) {
        console.warn('Auth token invalid or expired:', error?.response?.status || error.message);
        localStorage.removeItem('quickhop_token');
        setToken(null);
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('quickhop_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role = 'rider', phone = null) => {
    const response = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      role,
      phone
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('quickhop_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const processGoogleAuth = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    // For Google OAuth, we'll use the session token from cookie
    // Store a flag that we're using session auth
    localStorage.setItem('quickhop_auth_type', 'google');
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
    } catch (error) {
      // Non-fatal: server-side logout may fail if the token is already expired.
      // We still proceed to clear local state so the client-side session ends.
      console.warn('Server-side logout failed (proceeding to clear local session):', error?.message);
    }
    localStorage.removeItem('quickhop_token');
    localStorage.removeItem('quickhop_auth_type');
    setToken(null);
    setUser(null);
  };

  const switchRole = async (newRole) => {
    const response = await axios.put(
      `${API}/auth/role`,
      { role: newRole },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      }
    );
    setUser(prev => ({ ...prev, role: newRole }));
    return response.data;
  };

  const getAuthHeaders = () => {
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      login,
      register,
      logout,
      processGoogleAuth,
      switchRole,
      getAuthHeaders,
      checkAuth,
      setUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
